import * as React from 'react';

import { Box } from '@lightspeed/flame/Core';
import { useFieldArray } from 'react-hook-form';

import { useLabelPrintForm } from './hooks';
import { StyledLabelWithoutMargin, StyledDescription } from './HubLabelPrintBody.style';
import { StyledInput, StyledButton, StyledInputGroup, StyledError } from './InputStepper.style';

interface InputStepperProps {
  labelText: string;
  descriptionText?: string;
  inputRef:
    | 'quantity.labels'
    | 'default'
    | 'labelMargins.small'
    | 'labelMargins.normal'
    | 'labelMargins.alt'
    | 'labelMargins.jewelry';
  isInputDisabled?: boolean;
  isIncrementButtonDisabled?: boolean;
  isDecrementButtonDisabled?: boolean;
  isInputRequired?: boolean;
  maxInputValue: number;
  minInputValue: number;
  printForm: ReturnType<typeof useLabelPrintForm>;
  placeholder?: string | undefined;
  status?: undefined | 'error';
  stepperFunction: (amount: number) => () => void;
}

export const InputStepper: React.FC<InputStepperProps> = ({
  labelText,
  descriptionText,
  inputRef,
  isInputDisabled = false,
  isIncrementButtonDisabled = false,
  isDecrementButtonDisabled = false,
  maxInputValue,
  minInputValue,
  printForm,
  placeholder = '0',
  status = undefined,
  stepperFunction,
}) => {
  const { control, register, getValues } = printForm;
  const { fields } = useFieldArray({
    control,
    name: 'fieldArray',
  });

  const isLabelMarginsInputStepper = [
    'labelMargins.small',
    'labelMargins.normal',
    'labelMargins.alt',
    'labelMargins.jewelry',
  ].includes(inputRef);

  return (
    <>
      <Box mb={1}>
        <label htmlFor="input-stepper">
          <StyledLabelWithoutMargin>{labelText}</StyledLabelWithoutMargin>
        </label>
        <StyledDescription>{descriptionText}</StyledDescription>
      </Box>
      <StyledInputGroup>
        <StyledButton
          data-test={`input-stepper-button-decrement-${inputRef}`}
          onClick={stepperFunction(-1)}
          disabled={isDecrementButtonDisabled}
        >
          &#8722;
        </StyledButton>
        {fields
          .filter(item => item.name === inputRef)
          .map(field => {
            return (
              <StyledInput
                data-test={inputRef}
                type="number"
                key={field.id}
                id="input-stepper"
                readOnly={isInputDisabled}
                disabled={isInputDisabled}
                placeholder={placeholder}
                defaultValue={getValues(field.name) as number}
                status={status}
                {...register(field.name, {
                  max: maxInputValue,
                  min: minInputValue,
                  valueAsNumber: true,
                })}
              />
            );
          })}
        <StyledButton
          data-test={`input-stepper-button-increment-${inputRef}`}
          onClick={stepperFunction(1)}
          disabled={isIncrementButtonDisabled}
        >
          &#43;
        </StyledButton>
      </StyledInputGroup>
      <Box>
        {isLabelMarginsInputStepper && status && (
          <StyledError data-test="label-margins-error">
            Ensure that the label margins are between -20 and 20.
          </StyledError>
        )}
      </Box>
    </>
  );
};
